/**
 * ShadowNoteViewerDialog
 *
 * Read-only dialog that displays a shadow note's sections.
 * Polls while status is "processing".
 */

import { useEffect, useState } from 'react';
import { MarkdownNoteSectionDisplayBox } from '@components/Note/MarkdownNoteSectionDisplayBox';
import { Close, ContentCopy, Delete } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { useDeleteShadowNoteMutation, useGetShadowNoteQuery } from '../store/shadowNotesApi';
import type { ShadowNoteSection } from '../types/shadow-notes';
import { formatDateTime, getStatusBadgeColor } from '../utils/formatting';

const TERMINAL = new Set(['completed', 'error']);

function pollingMs(status: string | null | undefined): number | undefined {
  return !status || TERMINAL.has(status) ? undefined : 3000;
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="caption" sx={{ maxWidth: '55%', textAlign: 'right', wordBreak: 'break-all' }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  shadowNoteId: string;
  organizationId: string;
}

export function ShadowNoteViewerDialog({ open, onClose, shadowNoteId, organizationId }: Props) {
  const [interval, setInterval_] = useState<number | undefined>(3000);

  const { data: note, isLoading, error } = useGetShadowNoteQuery(
    { shadowNoteId, organizationId },
    { skip: !shadowNoteId || !open, pollingInterval: interval },
  );

  const [deleteShadowNote, { isLoading: isDeleting }] = useDeleteShadowNoteMutation();

  useEffect(() => {
    setInterval_(pollingMs(note?.status));
  }, [note?.status]);

  const handleDelete = async () => {
    await deleteShadowNote({ shadowNoteId, organizationId }).unwrap();
    onClose();
  };

  const handleCopyId = () => {
    void navigator.clipboard.writeText(shadowNoteId);
  };

  const status = note?.status ?? 'processing';
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- generated JSONB
  const sections: ShadowNoteSection[] = note?.content?.sections ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"lg"}>
      <DialogTitle sx={{ pr: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isLoading && !note ? (
              <Typography variant="h6">Loading shadow note...</Typography>
            ) : error ? (
              <Typography variant="h6" color="error.main">Error loading shadow note</Typography>
            ) : (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" noWrap>
                    {note?.note_title || 'Shadow Note'}
                  </Typography>
                  <Chip label={status} color={getStatusBadgeColor(status)} size="small" />
                  {status === 'processing' && <CircularProgress size={16} />}
                </Box>
                <Stack direction="row" spacing={1} sx={{alignItems: "center",  mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {shadowNoteId}
                  </Typography>
                  <IconButton size="small" onClick={handleCopyId} title="Copy ID">
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              </>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ overflow: 'auto' }}>
        {isLoading && !note && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && !isLoading && (
          <Alert severity="error">Unable to load shadow note.</Alert>
        )}

        {note && !error && (
          <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Metadata sidebar */}
            <Box sx={{ minWidth: 240, maxWidth: 280, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Metadata</Typography>
              <Divider sx={{ mb: 1 }} />
              <Meta label="Template" value={note.template_title} />
              <Meta label="Version" value={note.template_version != null ? `v${note.template_version}` : 'current'} />
              <Meta label="Organization" value={note.organization_id} />
              <Meta label="Encounter" value={note.encounter_id} />
              <Meta label="Patient" value={note.patient_id} />
              <Meta label="Provider" value={note.user_id} />
              <Meta label="Created By" value={note.created_by} />
              <Meta label="Created" value={formatDateTime(note.created_at)} />
              <Meta label="Updated" value={formatDateTime(note.updated_at)} />

              {status === 'error' && note.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {note.error_message}
                </Alert>
              )}
            </Box>

            {/* Sections */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Sections ({sections.length})
              </Typography>

              {status === 'processing' && sections.length === 0 ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 8 }}>
                  <CircularProgress size={24} />
                  <Typography color="text.secondary">Generating note sections...</Typography>
                </Box>
              ) : sections.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  No sections generated.
                </Typography>
              ) : (
                <Stack spacing={3}>
                  {sections.map((section, i) => (
                    <Box key={section.section_id ?? i}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {section.name}
                      </Typography>
                      {section.error ? (
                        <Alert severity="error">{section.error}</Alert>
                      ) : (
                        <MarkdownNoteSectionDisplayBox
                          text={section.edited_text || section.text || ''}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          color="error"
          startIcon={<Delete />}
          onClick={() => void handleDelete()}
          disabled={isDeleting}
          size="small"
        >
          Delete
        </Button>
        <Box  sx={{ flex: 1 }}/>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
